"use client";

import * as React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CircleCheck, ArrowRight, Map, Compass, Ruler, HardHat, Building2, Mountain } from "lucide-react";
const servicesData = [{
  id: "professionals",
  code: "RX-FAST",
  title: "Busy Professionals",
  description: "High-speed delivery designed for the Atlanta pace. We coordinate directly with your doctor to ensure your prescriptions arrive at your office or home without missing a beat.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/atlanta_professional_delivery.png",
  icon: Map,
  features: ["Same-day priority routing", "Office-safe discreet packaging", "Real-time GPS tracking", "Direct doctor verification"]
}, {
  id: "elderly",
  code: "RX-CARE",
  title: "Elderly Care Support",
  description: "Safety-first medication management for seniors. Our couriers are trained in healthcare compliance to provide a secure, respectful, and reliable delivery experience.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/senior_healthcare_support.png",
  icon: Mountain,
  features: ["In-person hand-off protocols", "Easy-open safety packaging", "Caregiver notification alerts", "Recurring schedule management"]
}, {
  id: "chronic",
  code: "RX-SYNC",
  title: "Chronic Management",
  description: "Consistency you can count on. We synchronize multiple prescriptions into a single monthly delivery, ensuring you never run out of essential maintenance medications.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/chronic_medication_sync.png",
  icon: Compass,
  features: ["Multi-medication synchronization", "Automatic refill coordination", "Insurance billing assistance", "Dedicated patient advocate"]
}, {
  id: "doctor-network",
  code: "RX-DOC",
  title: "Doctor-Integrated Logistics",
  description: "A seamless bridge between your physician and your doorstep. Our platform integrates with top Atlanta medical practices for instant prescription validation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/medical_practice_integration.png",
  icon: HardHat,
  features: ["HIPAA-compliant data transfer", "Instant digital script intake", "Pharmacy-to-door chain of custody", "Verified medical courier network"]
}, {
  id: "specialty",
  code: "RX-SPEC",
  title: "Specialty Medications",
  description: "Expert handling for sensitive treatments. We provide temperature-controlled transport for biologics and other high-value specialty pharmaceuticals.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/specialty_pharmacy_logistics.png",
  icon: Ruler,
  features: ["Cold-chain storage solutions", "Sensitive handling protocols", "Signature-required security", "Specialty pharmacy liaison"]
}, {
  id: "concierge",
  code: "RX-VIP",
  title: "Concierge Pharmacy Service",
  description: "The gold standard of prescription access. Personalized support that handles every detail of your medication needs with clinical luxury and precision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/concierge_medical_delivery.png",
  icon: Building2,
  features: ["24/7 VIP support access", "White-glove delivery service", "Customized delivery windows", "Comprehensive health reporting"]
}];
export default function MainServices() {
  return <section data-section-id="239763" id="tailored-delivery-solutions" className="bg-background text-foreground py-24 lg:py-32 relative">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 font-default tracking-tight">
            Tailored Delivery Solutions
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            GOTMEDS.IO provides specialized prescription logistics for Atlanta's diverse needs, 
            combining clinical precision with the speed of modern delivery.
          </p>
        </div>

        <Tabs defaultValue={servicesData[0].id} className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          
          <TabsList className="flex-col h-auto w-full lg:w-1/3 items-stretch bg-transparent p-0 gap-3">
            {servicesData.map((service, i) => {
            const Icon = service.icon;
            return <TabsTrigger key={service.id} value={service.id} data-index={i} className="justify-start text-left px-6 py-5 h-auto rounded-xl border border-border bg-card hover:border-primary hover:-translate-y-1 data-[state=active]:border-primary data-[state=active]:bg-primary/5 data-[state=active]:shadow-lg transition-all duration-300 group relative overflow-hidden whitespace-normal">
                  <div className="flex items-center gap-4 w-full">
                    <div className="p-2 rounded-lg bg-muted text-muted-foreground group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground transition-colors">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="font-default text-base font-semibold text-foreground group-data-[state=active]:text-primary transition-colors flex-1">
                      {service.title}
                    </span>
                  </div>
                  
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 font-mono text-xs font-medium text-primary bg-background/90 px-2 py-1 rounded border border-primary/20 shadow-sm backdrop-blur-sm translate-x-2 group-hover:translate-x-0">
                    {service.code}
                  </span>
                </TabsTrigger>;
          })}
          </TabsList>

          <div className="w-full lg:w-2/3">
            {servicesData.map((service, i) => <TabsContent key={service.id} value={service.id} data-index={i} className="mt-0 outline-none focus-visible:ring-0">
                <div className="bg-card border border-border rounded-2xl p-6 md:p-8 lg:p-10 shadow-xl flex flex-col gap-8 animate-in fade-in slide-in-from-right-8 duration-500 fill-mode-both">
                  
                  <div className="relative h-64 md:h-80 w-full rounded-xl overflow-hidden group">
                    <img src={service.image} alt={service.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                      <h3 className="text-2xl md:text-3xl font-bold text-white font-default max-w-lg leading-tight">
                        {service.title}
                      </h3>
                      <span className="hidden md:inline-block font-mono text-sm text-white/90 bg-primary/40 px-3 py-1.5 rounded backdrop-blur-md border border-white/20">
                        {service.code}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
                    <div className="flex-1">
                      <p className="text-muted-foreground mb-8 text-lg leading-relaxed font-default">
                        {service.description}
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="font-default font-semibold text-foreground text-lg">
                          Service Benefits:
                        </h4>
                        <ul className="space-y-3">
                          {service.features.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-3">
                              <CircleCheck className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                              <span className="text-foreground font-default">{feature}</span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="md:w-1/3 flex flex-col justify-end pt-6 md:pt-0 border-t md:border-t-0 md:border-l border-border md:pl-8">
                      <div className="bg-accent rounded-xl p-6 text-center mb-6">
                        <p className="text-sm text-muted-foreground mb-2 font-default">
                          Ready to start your delivery?
                        </p>
                        <p className="font-default font-semibold text-foreground">
                          Our team coordinates with your Atlanta doctor immediately.
                        </p>
                      </div>
                      <Button size="lg" className="w-full group bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg" asChild>
                        <Link to="#">
                          Get Started
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                </div>
              </TabsContent>)}
          </div>

        </Tabs>
      </div>
    </section>;
}